import { INDUSTRY_DATABASE, type IndustryData } from "./industry-cpc-database";

export type Verdict = "solid" | "tight" | "problematic";

export interface ViabilityInputs {
  aov: number;
  grossMargin: number; // %
  marketingMargin: number; // %
  conversionRate: number; // %
  industrySlug: string;
}

export interface ViabilityResults {
  maxCPA: number;
  maxCPC: number;
  breakevenConversionRate: number; // %
  cpcGapToMedianPct: number; // signed %
  verdict: Verdict;
  industry: IndustryData;
}

export function calculateViability(inputs: ViabilityInputs): ViabilityResults {
  const industry = INDUSTRY_DATABASE[inputs.industrySlug];
  if (!industry) throw new Error(`Unknown industry: ${inputs.industrySlug}`);

  const maxCPA =
    (inputs.aov * (inputs.grossMargin - inputs.marketingMargin)) / 100;
  const maxCPC = (maxCPA * inputs.conversionRate) / 100;
  const breakevenConversionRate =
    maxCPA > 0 ? (industry.medianCPC / maxCPA) * 100 : 0;
  const cpcGapToMedianPct =
    industry.medianCPC > 0
      ? ((maxCPC - industry.medianCPC) / industry.medianCPC) * 100
      : 0;

  // Verdict-Schwellen (symmetrisch ±30 % um Branchen-Median):
  //   Solid       > +30 % über Branchen-Median
  //   Tight       −30 % … +30 % zum Branchen-Median
  //   Problematic < −30 % unter Branchen-Median
  let verdict: Verdict;
  if (maxCPC > industry.medianCPC * 1.3) verdict = "solid";
  else if (maxCPC >= industry.medianCPC * 0.7) verdict = "tight";
  else verdict = "problematic";

  return {
    maxCPA: round2(maxCPA),
    maxCPC: round2(maxCPC),
    breakevenConversionRate: round2(breakevenConversionRate),
    cpcGapToMedianPct: round2(cpcGapToMedianPct),
    verdict,
    industry,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
